import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Plus, Trash2, Save, Camera, X as XIcon, Eye, EyeOff, ExternalLink, BookOpen,
  Heading2, Heading3, Bold, Italic, List, ListOrdered, Quote, Link as LinkIcon, ImagePlus, Sparkles,
} from "lucide-react";
import { Card, Btn, Input, Field } from "@/components/admin/ui";
import { API, BACKEND_URL } from "@/config";
import { fmtErr } from "@/context/AuthProvider";

const emptyPost = {
  title: "",
  slug: "",
  body: "",
  excerpt: "",
  image: "",
  date: new Date().toISOString().slice(0, 10),
  read_time: "",
  published: false,
  featured_product_id: "",
  seo_title: "",
  seo_description: "",
};

// Small helper so image previews handle both external URLs and /api/… storage paths.
const resolveImg = (src) => (src && src.startsWith("/api/") ? `${BACKEND_URL}${src}` : src);

export const BlogPosts = () => {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, p] = await Promise.all([
        axios.get(`${API}/admin/blog`),
        axios.get(`${API}/admin/products`),
      ]);
      setItems(b.data.posts || []);
      // Only products that are still bookable — otherwise the "Book This Item" button
      // on the public article page would land on a dead product.
      setProducts((p.data.products || []).filter((x) => x.active !== false));
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Failed to load journal");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/blog/${post.id}`);
      toast.success("Article deleted");
      load();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Delete failed");
    }
  };

  const togglePublish = async (post) => {
    try {
      await axios.patch(`${API}/admin/blog/${post.id}`, { published: !post.published });
      toast.success(post.published ? "Unpublished" : "Published ✨");
      load();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Update failed");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-4xl">Field Journal</h1>
          <p className="text-xs text-[#B9C8D5] mt-1">Bayou-wisdom articles that appear on your home page. Unpublished drafts stay hidden from customers.</p>
        </div>
        <Btn onClick={() => setEditing({ ...emptyPost })} data-testid="blog-add-btn"><Plus className="w-3.5 h-3.5" /> New Article</Btn>
      </div>

      {loading ? (
        <div className="text-center text-[#B9C8D5] py-16">Loading…</div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center" data-testid="blog-empty">
          <BookOpen className="w-10 h-10 mx-auto text-[#FFD166] mb-3" />
          <div className="font-serif text-2xl">No articles yet</div>
          <div className="text-sm text-[#B9C8D5] mt-2">Publish your first Field Journal entry to help families plan their Disney trip.</div>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((p) => (
            <Card key={p.id} className="p-5" data-testid={`blog-row-${p.slug}`}>
              <div className="grid md:grid-cols-[100px_1fr_auto] gap-4 items-center">
                {p.image ? (
                  <img src={resolveImg(p.image)} alt="" className="w-24 h-24 object-cover rounded-lg" />
                ) : (
                  <div className="w-24 h-24 bg-black/40 rounded-lg flex items-center justify-center text-[#B9C8D5]"><BookOpen className="w-6 h-6" /></div>
                )}
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] uppercase tracking-[0.22em] px-2 py-0.5 rounded-full border ${p.published ? "border-[#3A5A40]/60 text-[#8FBF9F] bg-[#3A5A40]/15" : "border-[#B9C8D5]/40 text-[#B9C8D5] bg-black/30"}`} data-testid={`blog-status-${p.slug}`}>
                      {p.published ? "Published" : "Draft"}
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[#FFD166]">/journal/{p.slug}</span>
                    {p.featured_product_id && (
                      <span className="text-[10px] uppercase tracking-[0.22em] text-[#F4A93A] border border-[#F4A93A]/30 bg-[#F4A93A]/10 px-2 py-0.5 rounded-full">
                        Featured: {p.featured_product_id}
                      </span>
                    )}
                  </div>
                  <div className="font-serif text-2xl mt-1 truncate">{p.title}</div>
                  <div className="text-sm text-[#B9C8D5] mt-1 line-clamp-2">{p.excerpt}</div>
                  <div className="mt-2 text-[11px] text-[#B9C8D5] flex gap-4 flex-wrap">
                    <span>Date: <span className="text-[#F6F1E4]">{p.date}</span></span>
                    <span>Read: <span className="text-[#F6F1E4]">{p.read_time}</span></span>
                  </div>
                </div>
                <div className="flex flex-col gap-2 items-stretch">
                  <Btn variant="ghost" onClick={() => togglePublish(p)} data-testid={`blog-toggle-publish-${p.slug}`}>
                    {p.published ? <><EyeOff className="w-3.5 h-3.5" /> Unpublish</> : <><Eye className="w-3.5 h-3.5" /> Publish</>}
                  </Btn>
                  <Btn variant="ghost" onClick={() => setEditing({ ...p })} data-testid={`blog-edit-${p.slug}`}>Edit</Btn>
                  {p.published && (
                    <a href={`/journal/${p.slug}`} target="_blank" rel="noreferrer" className="text-[11px] text-[#B9C8D5] hover:text-[#FFD166] inline-flex items-center gap-1 justify-center" data-testid={`blog-view-${p.slug}`}>
                      <ExternalLink className="w-3 h-3" /> View
                    </a>
                  )}
                  <Btn variant="danger" onClick={() => del(p)} data-testid={`blog-delete-${p.slug}`}><Trash2 className="w-3.5 h-3.5" /></Btn>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <BlogPostForm
          draft={editing}
          products={products}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
};

const BlogPostForm = ({ draft, products, onClose, onSaved }) => {
  const [f, setF] = useState({ ...draft });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [inlineUploading, setInlineUploading] = useState(false);
  const [preview, setPreview] = useState(false);
  const bodyRef = useRef(null);
  const isNew = !draft.id;

  const save = async () => {
    if (!f.title.trim()) { toast.error("Title is required"); return; }

    // Guard: changing a published article's slug without explicit confirmation
    if (!isNew && draft.published && f.slug && draft.slug && f.slug.trim() !== draft.slug.trim()) {
      const confirmed = window.confirm(
        "Changing this published article's URL may affect existing links and Google indexing. " +
        "Automatic redirects are not yet enabled for blog articles. " +
        "Continue only if you are prepared to add a redirect."
      );
      if (!confirmed) return;
    }

    setSaving(true);
    try {
      const payload = {
        title: f.title.trim(),
        slug: (f.slug || "").trim() || undefined,
        body: f.body || "",
        excerpt: f.excerpt || "",
        image: f.image || "",
        date: f.date || undefined,
        read_time: f.read_time || "",
        published: !!f.published,
        featured_product_id: f.featured_product_id || null,
        seo_title: f.seo_title || "",
        seo_description: f.seo_description || "",
      };
      if (isNew) {
        await axios.post(`${API}/admin/blog`, payload);
      } else {
        await axios.patch(`${API}/admin/blog/${f.id}`, payload);
      }
      toast.success(isNew ? "Article created" : "Saved");
      onSaved();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await axios.post(`${API}/admin/upload-image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((prev) => ({ ...prev, image: data.url }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  // Wraps or inserts markdown at the current textarea cursor position so
  // clicking a toolbar button feels editorial, not modal.
  const insertMd = (before, after = "", placeholder = "") => {
    const el = bodyRef.current;
    if (!el) return;
    const { selectionStart: start, selectionEnd: end, value } = el;
    const selected = value.slice(start, end) || placeholder;
    const next = value.slice(0, start) + before + selected + after + value.slice(end);
    setF((prev) => ({ ...prev, body: next }));
    // Restore cursor just after the inserted text, focus back on the textarea.
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + before.length + selected.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const insertBlock = (block) => {
    const el = bodyRef.current;
    if (!el) return;
    const { selectionStart: start, value } = el;
    // If we're mid-line, drop into a new paragraph.
    const needsLeadingBreak = start > 0 && value[start - 1] !== "\n";
    const insertion = (needsLeadingBreak ? "\n\n" : "") + block + "\n\n";
    const next = value.slice(0, start) + insertion + value.slice(start);
    setF((prev) => ({ ...prev, body: next }));
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + insertion.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const uploadInlineImage = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setInlineUploading(true);
    try {
      const { data } = await axios.post(`${API}/admin/upload-image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const alt = file.name.replace(/\.[a-z]+$/i, "").replace(/[-_]+/g, " ");
      insertBlock(`![${alt}](${data.url})`);
      toast.success("Inline image inserted");
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || "Upload failed");
    } finally {
      setInlineUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 sm:p-6 overflow-y-auto" data-testid="blog-form-modal">
      <Card className="p-6 w-full max-w-3xl my-6 max-h-[calc(100vh-3rem)] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-2xl">{isNew ? "New Article" : `Edit · ${draft.title}`}</h2>
          <button onClick={onClose} className="text-[#B9C8D5] hover:text-[#FFD166]" data-testid="blog-form-close"><XIcon className="w-5 h-5" /></button>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Title">
            <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="bf-title" placeholder="Your First Disney Trip with a Toddler…" />
          </Field>
          <Field label="URL slug (auto from title if blank)">
            <Input value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} data-testid="bf-slug" placeholder="first-disney-trip" />
          </Field>
          <Field label="Publish date">
            <Input type="date" value={f.date || ""} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="bf-date" />
          </Field>
          <Field label="Read time (auto if blank)">
            <Input value={f.read_time || ""} onChange={(e) => setF({ ...f, read_time: e.target.value })} placeholder="e.g. 5 min" data-testid="bf-read-time" />
          </Field>
          <Field label="Featured rental (optional)">
            <select
              value={f.featured_product_id || ""}
              onChange={(e) => setF({ ...f, featured_product_id: e.target.value })}
              className="w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg"
              data-testid="bf-featured-product"
            >
              <option value="">— None —</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>{p.name} · {p.category}</option>
              ))}
            </select>
            <p className="text-[10px] text-[#B9C8D5] mt-1">Shows a "Book This Item" button on the article page linking to the product.</p>
          </Field>
          <Field label="Publish state">
            <label className="flex items-center gap-2 mt-2 cursor-pointer" data-testid="bf-published">
              <input type="checkbox" checked={!!f.published} onChange={(e) => setF({ ...f, published: e.target.checked })} className="accent-[#FFD166]" />
              <span className="text-sm text-[#F6F1E4]">{f.published ? "Published — visible on the home page" : "Draft — hidden from customers"}</span>
            </label>
          </Field>
        </div>

        <div className="mt-4">
          <label className="text-xs uppercase tracking-[0.2em] text-[#B9C8D5]">Cover photo</label>
          <Input value={f.image} onChange={(e) => setF({ ...f, image: e.target.value })} placeholder="Paste a URL or upload from device" data-testid="bf-image-url" className="mt-1" />
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            <label className="inline-flex items-center gap-2 text-xs text-[#FFD166] cursor-pointer hover:underline" data-testid="bf-image-upload-label">
              <input
                type="file"
                accept="image/*"
                className="hidden"
                data-testid="bf-image-upload"
                onChange={(e) => uploadPhoto(e.target.files?.[0])}
              />
              <Camera className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : "Upload photo from device"}
            </label>
            {f.image && (
              <img src={resolveImg(f.image)} alt="" className="w-24 h-24 object-cover rounded-lg border border-[#FFD166]/25" />
            )}
          </div>
        </div>

        <div className="mt-4">
          <label className="text-xs uppercase tracking-[0.2em] text-[#B9C8D5]">Excerpt (shown on card, auto from body if blank)</label>
          <textarea
            rows="2"
            value={f.excerpt || ""}
            onChange={(e) => setF({ ...f, excerpt: e.target.value })}
            className="mt-1 w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg"
            placeholder="One or two sentences that tease the article on the home page."
            data-testid="bf-excerpt"
          />
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <label className="text-xs uppercase tracking-[0.2em] text-[#B9C8D5]">Article body <span className="normal-case tracking-normal text-[#B9C8D5]/70">· Markdown supported</span></label>
            <button
              type="button"
              onClick={() => setPreview((v) => !v)}
              className="text-[11px] text-[#FFD166] hover:underline inline-flex items-center gap-1"
              data-testid="bf-body-toggle-preview"
            >
              <Eye className="w-3 h-3" /> {preview ? "Edit" : "Preview"}
            </button>
          </div>

          {/* Toolbar — thin editorial control strip. Ordered by what an owner
              writing a magazine post reaches for most: structure, then emphasis,
              then media. */}
          <div className="mt-1 flex flex-wrap items-center gap-1 rounded-t-lg border border-[#FFD166]/20 border-b-0 bg-black/40 px-2 py-1.5" data-testid="bf-toolbar">
            <ToolbarBtn title="Heading 2" onClick={() => insertBlock("## Section heading")} testid="bf-tool-h2"><Heading2 className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn title="Heading 3" onClick={() => insertBlock("### Subheading")} testid="bf-tool-h3"><Heading3 className="w-4 h-4" /></ToolbarBtn>
            <ToolbarDivider />
            <ToolbarBtn title="Bold" onClick={() => insertMd("**", "**", "bold text")} testid="bf-tool-bold"><Bold className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn title="Italic" onClick={() => insertMd("*", "*", "italic text")} testid="bf-tool-italic"><Italic className="w-4 h-4" /></ToolbarBtn>
            <ToolbarDivider />
            <ToolbarBtn title="Bulleted list" onClick={() => insertBlock("- First item\n- Second item\n- Third item")} testid="bf-tool-ul"><List className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn title="Numbered list" onClick={() => insertBlock("1. First step\n2. Second step\n3. Third step")} testid="bf-tool-ol"><ListOrdered className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn title="Block quote" onClick={() => insertBlock("> Something worth pulling out and reading twice.")} testid="bf-tool-quote"><Quote className="w-4 h-4" /></ToolbarBtn>
            <ToolbarDivider />
            <ToolbarBtn title="Link" onClick={() => {
              const url = window.prompt("Link URL", "https://");
              if (!url) return;
              insertMd("[", `](${url})`, "link text");
            }} testid="bf-tool-link"><LinkIcon className="w-4 h-4" /></ToolbarBtn>
            <label className="inline-flex items-center gap-1 px-2 py-1 rounded text-[#B9C8D5] hover:text-[#FFD166] hover:bg-[#FFD166]/10 cursor-pointer" title="Insert image" data-testid="bf-tool-image-label">
              <input type="file" accept="image/*" className="hidden" data-testid="bf-tool-image-upload" onChange={(e) => uploadInlineImage(e.target.files?.[0])} />
              <ImagePlus className="w-4 h-4" />
              {inlineUploading && <span className="text-[10px]">Uploading…</span>}
            </label>
          </div>

          {preview ? (
            <div
              className="rounded-b-lg border border-[#FFD166]/20 bg-black/25 px-4 py-4 min-h-[280px] blog-article-body"
              data-testid="bf-body-preview"
            >
              {f.body?.trim() ? (
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={previewMdComponents}>
                  {f.body}
                </ReactMarkdown>
              ) : (
                <div className="text-sm text-[#B9C8D5] italic">Nothing to preview yet. Start writing on the Edit tab.</div>
              )}
            </div>
          ) : (
            <textarea
              ref={bodyRef}
              rows="16"
              value={f.body || ""}
              onChange={(e) => setF({ ...f, body: e.target.value })}
              className="w-full bg-black/30 border border-[#FFD166]/25 border-t-transparent text-[#F6F1E4] px-3 py-2 rounded-b-lg font-mono text-sm focus:outline-none focus:border-[#FFD166]"
              placeholder={"Write with Markdown:\n\n## A section heading\n\nA paragraph of prose. **Bold important words** and add a [helpful link](https://example.com).\n\n- Bullet one\n- Bullet two\n\n> A pull quote your readers will remember.\n\n![alt text](https://…)"}
              data-testid="bf-body"
            />
          )}
          <p className="text-[10px] text-[#B9C8D5] mt-1">Markdown: <code className="text-[#FFD166]">##</code> heading · <code className="text-[#FFD166]">**bold**</code> · <code className="text-[#FFD166]">*italic*</code> · <code className="text-[#FFD166]">[text](url)</code> · <code className="text-[#FFD166]">-</code> list · <code className="text-[#FFD166]">&gt;</code> quote · <code className="text-[#FFD166]">![alt](url)</code> image.</p>
        </div>

        <div className="mt-6 border-t border-[#FFD166]/15 pt-5">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-[#F4A93A]" />
            <div className="text-xs uppercase tracking-[0.2em] text-[#F4A93A]">Search & social preview</div>
          </div>
          <p className="text-[11px] text-[#B9C8D5] mb-3">Leave blank to let Google, Facebook, and X use the article's title and excerpt automatically.</p>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="SEO title (overrides article title)">
              <Input
                value={f.seo_title || ""}
                onChange={(e) => setF({ ...f, seo_title: e.target.value })}
                maxLength={70}
                placeholder="Under 60 characters wins on Google"
                data-testid="bf-seo-title"
              />
              <div className="text-[10px] text-[#B9C8D5] mt-1">{(f.seo_title || "").length}/70</div>
            </Field>
            <Field label="SEO description (overrides excerpt)">
              <Input
                value={f.seo_description || ""}
                onChange={(e) => setF({ ...f, seo_description: e.target.value })}
                maxLength={200}
                placeholder="150–160 chars converts best"
                data-testid="bf-seo-description"
              />
              <div className="text-[10px] text-[#B9C8D5] mt-1">{(f.seo_description || "").length}/200</div>
            </Field>
          </div>
          <div className="mt-4 rounded-lg border border-[#FFD166]/15 bg-black/30 p-3" data-testid="bf-seo-preview">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#B9C8D5]">Google preview</div>
            <div className="mt-1 text-[#8FBF9F] text-xs truncate">enchantedgearrentals.com › journal › {(f.slug || (f.title || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)) || "your-article"}</div>
            <div className="mt-0.5 text-[#7c9ae7] text-base leading-tight line-clamp-1">{f.seo_title || f.title || "Article title"}</div>
            <div className="mt-1 text-[11px] text-[#B9C8D5] line-clamp-2">{f.seo_description || f.excerpt || "Your excerpt will appear here so families know what the story is about."}</div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={save} disabled={saving} data-testid="blog-form-save"><Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save"}</Btn>
        </div>
      </Card>
    </div>
  );
};

// ---------- tiny toolbar primitives ----------
const ToolbarBtn = ({ title, onClick, children, testid }) => (
  <button
    type="button"
    title={title}
    onClick={onClick}
    className="inline-flex items-center gap-1 px-2 py-1 rounded text-[#B9C8D5] hover:text-[#FFD166] hover:bg-[#FFD166]/10 transition-colors"
    data-testid={testid}
  >
    {children}
  </button>
);
const ToolbarDivider = () => <span className="w-px h-4 bg-[#FFD166]/20 mx-1" />;

// Preview map — mirrors the public article map so what admins see is what
// customers see. Kept local so BlogPosts.jsx stays self-contained.
const previewMdComponents = {
  h1: ({ node, ...p }) => <h1 className="font-serif text-2xl md:text-3xl mt-6 mb-3 text-[#F6F1E4] leading-tight" {...p} />,
  h2: ({ node, ...p }) => <h2 className="font-serif text-xl md:text-2xl mt-6 mb-3 text-[#F6F1E4] leading-tight" {...p} />,
  h3: ({ node, ...p }) => <h3 className="font-serif text-lg mt-5 mb-2 text-[#FFD166]" {...p} />,
  p: ({ node, ...p }) => <p className="text-[#E4E9EF] leading-relaxed font-light mb-3" {...p} />,
  a: ({ node, ...p }) => <a className="text-[#FFD166] underline decoration-[#FFD166]/40 underline-offset-2" {...p} />,
  strong: ({ node, ...p }) => <strong className="text-[#F6F1E4] font-semibold" {...p} />,
  em: ({ node, ...p }) => <em className="italic text-[#F6F1E4]" {...p} />,
  ul: ({ node, ...p }) => <ul className="list-disc pl-6 my-3 space-y-1.5 text-[#E4E9EF] font-light marker:text-[#FFD166]" {...p} />,
  ol: ({ node, ...p }) => <ol className="list-decimal pl-6 my-3 space-y-1.5 text-[#E4E9EF] font-light marker:text-[#FFD166]" {...p} />,
  li: ({ node, ...p }) => <li className="pl-1" {...p} />,
  blockquote: ({ node, ...p }) => <blockquote className="my-4 pl-4 border-l-4 border-[#FFD166]/60 italic text-[#F6F1E4]/90 text-lg font-serif" {...p} />,
  img: ({ node, src, alt, ...p }) => (
    <span className="block my-4">
      <img src={src?.startsWith("/api/") ? `${BACKEND_URL}${src}` : src} alt={alt || ""} className="w-full rounded-xl border border-[#FFD166]/15" {...p} />
      {alt && <span className="block mt-1 text-center text-[10px] text-[#B9C8D5] italic">{alt}</span>}
    </span>
  ),
  hr: () => <hr className="my-6 border-none h-px bg-[#FFD166]/20" />,
  code: ({ node, inline, ...p }) => inline
    ? <code className="px-1 py-0.5 rounded bg-black/40 text-[#FFD166] text-[0.9em] font-mono" {...p} />
    : <code className="block p-3 rounded bg-black/50 text-[#F6F1E4] text-xs font-mono overflow-x-auto" {...p} />,
};
